/**
 * Subscription & Plans API
 * გამოწერისა და გეგმების API
 */

import { get, post, getApiClient } from '@aiia/portal-core';

export type BillingCycle = 'MONTHLY' | 'YEARLY';
export type SubscriptionStatus = 'TRIAL' | 'ACTIVE' | 'CANCELED' | 'EXPIRED' | 'PAST_DUE' | 'GRACE_PERIOD' | 'READ_ONLY';
export type InvoiceStatus = 'PENDING' | 'PAID' | 'FAILED' | 'CANCELED' | 'REFUNDED' | 'OVERDUE' | 'DRAFT';

export interface Plan {
  code: string;
  name: string;
  description?: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  features: PlanFeature[];
  recommended?: boolean;
  isPopular?: boolean;
  isCurrent?: boolean;
}

export interface PlanFeature {
  code: string;
  name: string;
  value: number | boolean;
  displayValue?: string;
  unlimited?: boolean;
}

export interface Subscription {
  id: string;
  userId: string;
  planCode: string;
  planName: string;
  status: SubscriptionStatus;
  billingCycle: BillingCycle;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  trialEndsAt?: string;
  canceledAt?: string;
  createdAt: string;
}

export interface SubscriptionWithPlan extends Subscription {
  plan: Plan;
}

export interface UsageStats {
  conversationsUsed: number;
  conversationsLimit: number;
  apiCallsUsed: number;
  apiCallsLimit: number;
  widgetsUsed: number;
  widgetsLimit: number;
  apiKeysUsed: number;
  apiKeysLimit: number;
  dataFilesUsed: number;
  dataFilesLimit: number;
  periodStart: string;
  periodEnd: string;
}

export interface PlanComparison {
  currentPlan: Plan;
  availablePlans: Plan[];
}

export interface UpgradePlanRequest {
  planCode: string;
  billingCycle: BillingCycle;
  couponCode?: string;
}

export interface UpgradePlanResponse {
  invoiceId: string;
  amount: number;
  currency: string;
  paymentUrl?: string;
  orderId?: string;
}

export interface CanUpgradeResponse {
  canUpgrade: boolean;
  canDowngrade: boolean;
  planCode: string;
  message?: string;
}

export interface ChangePlanRequest {
  planCode: string;
  billingCycle: BillingCycle;
}

export interface CancelSubscriptionRequest {
  reason?: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  subtotal: number;
  discountAmount: number;
  taxAmount: number;
  total: number;
  currency: string;
  status: InvoiceStatus;
  billingPeriodStart?: string;
  billingPeriodEnd?: string;
  createdAt: string;
  dueDate?: string;
  paidAt?: string;
}

export interface Payment {
  id: string;
  invoiceId: string;
  amount: number;
  currency: string;
  status: string;
  paymentMethod?: string;
  transactionId?: string;
  createdAt: string;
}

// Get public plans (no auth required)
export const getPublicPlans = () =>
  get<Plan[]>('/public/plans');

// Get current subscription
export const getSubscription = (userId: string) =>
  get<SubscriptionWithPlan>(`/users/${userId}/subscription`);

// Get available plans
export const getPlans = (userId: string) =>
  get<Plan[]>(`/users/${userId}/subscription/plans`);

// Get usage statistics
export const getUsage = (userId: string) =>
  get<UsageStats>(`/users/${userId}/subscription/usage`);

// Change plan
export const changePlan = (userId: string, data: ChangePlanRequest) =>
  post<Subscription>(`/users/${userId}/subscription/change-plan`, data);

// Cancel subscription
export const cancelSubscription = (userId: string, data: CancelSubscriptionRequest) =>
  post<void>(`/users/${userId}/subscription/cancel`, data);

// Compare plans
export const comparePlans = () =>
  get<PlanComparison>('/subscription/compare-plans');

// Check if can upgrade to plan
export const canUpgrade = (planCode: string) =>
  get<CanUpgradeResponse>(`/subscription/can-upgrade/${planCode}`);

// Initiate upgrade
export const upgradePlan = (data: UpgradePlanRequest) =>
  post<UpgradePlanResponse>('/subscription/upgrade', data);

// Cancel pending upgrade
export const cancelUpgrade = (invoiceId: string) =>
  post<void>(`/subscription/cancel-upgrade/${invoiceId}`, {});

// Get invoices
export const getInvoices = (userId: string) =>
  get<Invoice[]>(`/users/${userId}/billing/invoices`);

// Get invoice details
export const getInvoice = (userId: string, invoiceId: string) =>
  get<Invoice>(`/users/${userId}/billing/invoices/${invoiceId}`);

// Get payments for invoice
export const getInvoicePayments = (userId: string, invoiceId: string) =>
  get<Payment[]>(`/users/${userId}/billing/invoices/${invoiceId}/payments`);

// Initiate payment
export const payInvoice = (userId: string, invoiceId: string) =>
  post<{ paymentId: string; paymentUrl: string; orderId: string }>(
    `/users/${userId}/billing/invoices/${invoiceId}/pay`,
    {}
  );

// Download invoice PDF
export const downloadInvoicePdf = async (userId: string, invoiceId: string, invoiceNumber: string) => {
  const response = await getApiClient().get(`/users/${userId}/billing/invoices/${invoiceId}/pdf`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.download = `invoice-${invoiceNumber}.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};
